/**
 * Turning a pasted reference list into one string per reference.
 *
 * People paste lists three ways: one reference per line, references separated by blank
 * lines (with hanging-indent wraps inside each), or a numbered list — `[1]` or `1.` —
 * whose long entries wrap onto unnumbered lines. All three come out the same: one
 * entry per reference, internal whitespace collapsed, a "References" heading dropped.
 */

const RE_CRLF = /\r\n?/g;
const RE_BLANK_LINE = /\n[ \t]*\n/;
const RE_NEWLINE = /\n/;
const RE_WHITESPACE_RUN = /\s+/g;
const RE_NUMBER_MARKER = /^(?:\[\d{1,4}\]|\d{1,4}[.)])\s/;
const RE_LIST_HEADING =
  /^(?:references?|reference list|bibliography|works cited|literature cited|sources|notes)\s*:?$/i;

function tidy(entry: string): string {
  return entry.replace(RE_WHITESPACE_RUN, " ").trim();
}

function isHeading(line: string): boolean {
  return RE_LIST_HEADING.test(line.trim());
}

/** Lines of a numbered list, with unnumbered wrap lines joined to the entry above. */
function joinNumbered(lines: string[]): string[] {
  const out: string[] = [];
  for (const line of lines) {
    if (RE_NUMBER_MARKER.test(line) || out.length === 0) {
      out.push(line);
    } else {
      out[out.length - 1] = `${out.at(-1)} ${line}`;
    }
  }
  return out;
}

export function splitReferences(text: string): string[] {
  const normalized = text.replace(RE_CRLF, "\n").trim();
  if (normalized === "") {
    return [];
  }
  if (RE_BLANK_LINE.test(normalized)) {
    return normalized
      .split(RE_BLANK_LINE)
      .map(tidy)
      .filter((entry) => entry !== "" && !isHeading(entry));
  }
  const lines = normalized
    .split(RE_NEWLINE)
    .map((line) => line.trim())
    .filter((line) => line !== "" && !isHeading(line));
  const numbered = lines.filter((line) => RE_NUMBER_MARKER.test(line)).length;
  const joined =
    numbered >= 2 && numbered < lines.length ? joinNumbered(lines) : lines;
  return joined.map(tidy).filter(Boolean);
}

const RE_DOI_IN_TEXT = /\b10\.\d{4,9}\/[^\s"'<>]+/i;
const RE_DOI_TRAILING = /[.,;:)\]}>]+$/;

/** The first DOI written anywhere in a string, without trailing punctuation. */
export function findDoi(text: string): string | null {
  const match = RE_DOI_IN_TEXT.exec(text);
  if (!match) {
    return null;
  }
  return match[0].replace(RE_DOI_TRAILING, "");
}
